// "Finding the best available lawyer…" screen — shown after the consultation
// payment is authorized while we broadcast the request to matched lawyers and
// wait for the first one to accept (socket `call-accepted`).
import { useEffect, useState } from "react";
import logo from "../../assets/images/logo.png";
import "./findingLawyer.css";

const STEPS = [
  { icon: "bi-person-vcard", label: "Checking lawyer available" },
  { icon: "bi-telephone-outbound", label: "Preparing your call" },
  { icon: "bi-clipboard-check", label: "Matching your case" },
  { icon: "bi-patch-check", label: "Verifying qualifications" },
];

const fmt = (total) => {
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};

/**
 * @param {object[]} lawyers   matched lawyers ({ id, name, picture }) for the orbit avatars
 * @param {number}   available count shown in the "Lawyers Available" row
 * @param {function} onCancel  cancel the search and release the request
 */
export default function FindingLawyer({ lawyers = [], available = 0, onCancel }) {
  const [seconds, setSeconds] = useState(0);
  // Reveal the checklist ticks one by one for a "working" feel.
  const [doneSteps, setDoneSteps] = useState(0);

  useEffect(() => {
    const tick = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (doneSteps >= STEPS.length) return;
    const id = setTimeout(() => setDoneSteps((n) => n + 1), 700);
    return () => clearTimeout(id);
  }, [doneSteps]);

  // Up to 6 avatars positioned around the orbit ring.
  const orbit = (lawyers.length ? lawyers : Array.from({ length: 6 })).slice(0, 6);

  return (
    <div className="fl-overlay">
      <div className="fl-wrap">
        <h2 className="fl-title">Finding the best available lawyer...</h2>
        <p className="fl-subtitle">Please wait while we connect you</p>

        <div className="fl-card">
          <div className="fl-left">
            <ul className="fl-steps">
              {STEPS.map((s, i) => (
                <li key={s.label} className={i < doneSteps ? "fl-step fl-step-done" : "fl-step"}>
                  <span className="fl-step-icon"><i className={`bi ${s.icon}`} /></span>
                  <span className="fl-step-label">{s.label}</span>
                  <span className="fl-step-check">
                    {i < doneSteps ? <i className="bi bi-check2" /> : <span className="fl-step-spin" />}
                  </span>
                </li>
              ))}
            </ul>

            <div className="fl-stats">
              <div className="fl-stat-row">
                <span className="fl-stat-label"><i className="bi bi-people-fill" /> Lawyers Available</span>
                <span className="fl-stat-value">{available}</span>
              </div>
              <div className="fl-stat-row">
                <span className="fl-stat-label"><i className="bi bi-clock-history" /> Avg. Connection Time</span>
                <span className="fl-stat-value">00:28</span>
              </div>
            </div>
          </div>

          <div className="fl-right">
            <div className="fl-radar">
              <span className="fl-ring fl-ring-1" />
              <span className="fl-ring fl-ring-2" />
              <span className="fl-ring fl-ring-3" />
              <div className="fl-orbit">
                {orbit.map((l, i) => (
                  <span
                    key={l?.id ?? i}
                    className="fl-avatar"
                    style={{ "--i": i, "--n": orbit.length }}
                  >
                    {l?.picture ? <img src={l.picture} alt={l?.name || "Lawyer"} /> : <i className="bi bi-person-fill" />}
                  </span>
                ))}
              </div>
              <div className="fl-core">
                <img src={logo} alt="Legal Platform" />
              </div>
            </div>
            <div className="fl-secure"><i className="bi bi-shield-lock-fill" /> Your call is secure and confidential</div>
          </div>
        </div>

        <button type="button" className="fl-cancel" onClick={onCancel}>
          <i className="bi bi-x-circle me-1" /> Cancel search
        </button>
        <div className="fl-elapsed">Searching… {fmt(seconds)}</div>
      </div>
    </div>
  );
}
